import {
  Typography as MuiTypography,
  TypographyProps as MuiTypographyProps,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';

/**
 * PeriVerse Themed Typography Component
 *
 * A customized typography component that follows the PeriVerse design system.
 * Extends Material-UI Typography with additional styling and variant options.
 */

// Styled typography with custom styling
const StyledTypography = styled(MuiTypography)(({ theme }) => ({
  // Additional custom styles can be added here
  // The theme overrides are already applied through the global theme
}));

export interface TypographyProps extends MuiTypographyProps {
  /**
   * The content of the typography
   */
  children: React.ReactNode;
}

/**
 * PeriVerse Typography Component
 *
 * A themed typography component that provides consistent text styling across the application.
 * Supports all Material-UI Typography props with additional PeriVerse-specific styling.
 *
 * @example
 * ```tsx
 * <Typography variant="h1" color="primary">
 *   Main Heading
 * </Typography>
 *
 * <Typography variant="body1" color="textSecondary">
 *   Body text content
 * </Typography>
 *
 * <Typography variant="caption" color="error">
 *   Error message
 * </Typography>
 *
 * <Typography variant="tableHeader">
 *   Table Header
 * </Typography>
 *
 * <Typography variant="tableCell">
 *   Table Cell Content
 * </Typography>
 * ```
 */
export const Typography: React.FC<TypographyProps> = ({
  variant = 'body1',
  color = 'textPrimary',
  align = 'inherit',
  display = 'initial',
  noWrap = false,
  children,
  ...props
}) => {
  return (
    <StyledTypography
      variant={variant}
      color={color}
      align={align}
      display={display}
      noWrap={noWrap}
      {...props}
    >
      {children}
    </StyledTypography>
  );
};

export default Typography;
